import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { RequireShop } from '../../components/ShopSelector';
import { SectionCard } from '../../components/PageChrome';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { slugify } from '../../utils/format';

const emptyCollection = {
  name: '',
  slug: '',
  description: '',
  meta_title: '',
  meta_description: '',
  is_active: true,
};

export default function CollectionsPage() {
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyCollection);
  const [saving, setSaving] = useState(false);

  const loadCollections = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await shopApi.listCollections(token, selectedShopId);
      setCollections(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedShopId]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const handleNameChange = (name) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: prev.slug || slugify(name),
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      await shopApi.createCollection(token, selectedShopId, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        meta_title: form.meta_title.trim() || null,
        meta_description: form.meta_description.trim() || null,
        is_active: form.is_active,
      });
      setShowForm(false);
      setForm(emptyCollection);
      await loadCollections();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RequireShop>
      <PageHeader
        title="Collections"
        description="Group products into curated collections for your storefront."
        actions={
          <>
            <Link to="/shop/products" className="btn btn-secondary">
              Products
            </Link>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowForm((open) => !open)}
            >
              {showForm ? 'Cancel' : 'Add collection'}
            </button>
          </>
        }
      />

      <ErrorBanner message={error} />

      {showForm && (
        <SectionCard title="New collection">
          <form className="form-grid" onSubmit={handleCreate}>
            <div className="form-grid two-col">
              <div className="form-field">
                <label htmlFor="collection-name">Title</label>
                <input
                  id="collection-name"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="collection-slug">URL handle</label>
                <input
                  id="collection-slug"
                  value={form.slug}
                  onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="collection-desc">Description</label>
              <textarea
                id="collection-desc"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="form-grid two-col">
              <div className="form-field">
                <label htmlFor="collection-meta-title">SEO title</label>
                <input
                  id="collection-meta-title"
                  value={form.meta_title}
                  onChange={(e) => setForm((p) => ({ ...p, meta_title: e.target.value }))}
                />
              </div>
              <div className="form-field">
                <label htmlFor="collection-meta-desc">SEO description</label>
                <input
                  id="collection-meta-desc"
                  value={form.meta_description}
                  onChange={(e) => setForm((p) => ({ ...p, meta_description: e.target.value }))}
                />
              </div>
            </div>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
              Active on storefront
            </label>
            <div className="page-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save collection'}
              </button>
            </div>
          </form>
        </SectionCard>
      )}

      {loading ? (
        <LoadingState />
      ) : collections.length === 0 ? (
        <EmptyState message="No collections yet. Create one, then assign products from the product editor." />
      ) : (
        <SectionCard title="All collections">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Handle</th>
                <th>Status</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {collections.map((collection) => (
                <tr key={collection.id}>
                  <td>
                    <strong>{collection.name}</strong>
                  </td>
                  <td>
                    <code>{collection.slug}</code>
                  </td>
                  <td>
                    <span className={`badge ${collection.is_active ? 'badge-success' : 'badge-muted'}`}>
                      {collection.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{collection.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}
    </RequireShop>
  );
}
